/**
 * Shared File Upload Utility
 *
 * Centralizes file validation, compression, and S3 upload logic
 * used across chat, forms, wiki, and SMS features.
 */

import axios from 'axios';
import { compressImageSimple } from './imageCompression';

// ---------------------------------------------------------------------------
// File category definitions with type allowlists and size limits
// ---------------------------------------------------------------------------

const FILE_CATEGORIES = {
  image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    types: [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'image/webp', 'image/svg+xml'
    ]
  },
  document: {
    maxSize: 25 * 1024 * 1024, // 25MB
    types: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'text/plain'
    ]
  },
  video: {
    maxSize: 50 * 1024 * 1024, // 50MB
    types: ['video/mp4', 'video/webm', 'video/quicktime']
  },
  audio: {
    maxSize: 50 * 1024 * 1024, // 50MB
    types: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm']
  }
};

// Blocked file types (security)
const BLOCKED_EXTENSIONS = /\.(php5?|html?|jsx?|exe|bat|cmd|sh|ps1)$/i;
const SYSTEM_FILES = /(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Determine the file category from a MIME type.
 * @param {string} mimeType
 * @returns {'image'|'document'|'video'|'audio'|null}
 */
export function getFileCategory(mimeType) {
  if (!mimeType) return null;
  const lower = mimeType.toLowerCase();
  for (const [category, config] of Object.entries(FILE_CATEGORIES)) {
    if (config.types.includes(lower)) return category;
  }
  return null;
}

/**
 * Validate a file against type allowlist and size limits.
 *
 * @param {File} file
 * @param {Object} [overrides] - Per-site overrides for size limits
 *   e.g. { image: { maxSize: 20971520 } }
 * @returns {{ valid: boolean, error: string|null, category: string|null }}
 */
export function validateFile(file, overrides = {}) {
  if (!file) {
    return { valid: false, error: 'No file provided', category: null };
  }

  // Block system files
  if (SYSTEM_FILES.test(file.name)) {
    return { valid: false, error: 'System files cannot be uploaded', category: null };
  }

  // Block dangerous extensions
  if (BLOCKED_EXTENSIONS.test(file.name)) {
    return { valid: false, error: `File type not allowed: ${file.name.split('.').pop()}`, category: null };
  }

  const category = getFileCategory(file.type);
  if (!category) {
    return { valid: false, error: `File type not supported: ${file.type || 'unknown'}`, category: null };
  }

  // Determine max size (per-site override > default)
  const categoryConfig = FILE_CATEGORIES[category];
  const maxSize = (overrides[category] && overrides[category].maxSize) || categoryConfig.maxSize;

  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0);
    return { valid: false, error: `File too large (${formatSize(file.size)}). Maximum: ${maxMB}MB`, category };
  }

  return { valid: true, error: null, category };
}

/**
 * Upload a file to S3 via presigned POST URL.
 *
 * @param {Object} params
 * @param {File}   params.file         - The file to upload
 * @param {string} params.siteID       - Site ID
 * @param {string} params.teamioType   - 'FORM'|'FORM_PDF'|'WIKI'|'CHAT'
 * @param {string} [params.patNum]     - Patient number (forms only)
 * @param {string} [params.roomId]     - Chat room ID (chat only)
 * @param {string} [params.messageId]  - Chat message ID (chat only)
 * @param {boolean} [params.compress=true] - Whether to compress images
 * @param {string} [params.endpoint]   - Override upload URL endpoint
 *   Defaults: '/get-form-upload-url' for forms, '/requestUploadURL' for everything else
 * @returns {Promise<{ url: string, key: string }>}
 */
export async function uploadToS3({
  file, siteID, teamioType, patNum, roomId, messageId,
  compress = true, endpoint
}) {
  // Compress images if enabled
  let fileToUpload = file;
  if (compress && getFileCategory(file.type) === 'image') {
    try {
      fileToUpload = await compressImageSimple(file);
    } catch (err) {
      console.error('Image compression failed, using original:', err);
    }
  }

  // Sanitize filename
  const safeName = sanitizeFilename(file.name);

  // Determine which endpoint to use
  const uploadEndpoint = endpoint || (teamioType === 'FORM' ? '/get-form-upload-url' : '/requestUploadURL');

  // Build request payload
  const requestData = {
    siteID,
    name: safeName,
    type: fileToUpload.type || file.type,
    teamioType
  };

  if (patNum) requestData.patNum = patNum;
  if (roomId) requestData.roomId = roomId;
  if (messageId) requestData.messageId = messageId;

  // Get presigned POST URL
  const { data: presignedData } = await axios.post(uploadEndpoint, requestData, {
    headers: { 'Content-Type': 'application/json' }
  });

  // Handle both response structures (some endpoints wrap in uploadURL)
  let uploadURL, uploadFields;
  if (presignedData.uploadURL) {
    uploadURL = presignedData.uploadURL.url;
    uploadFields = presignedData.uploadURL.fields;
  } else if (presignedData.url && presignedData.fields) {
    uploadURL = presignedData.url;
    uploadFields = presignedData.fields;
  } else {
    throw new Error('Invalid presigned URL response structure');
  }

  // Build FormData — all fields MUST come before 'file' (AWS ignores everything after)
  const formData = new FormData();
  Object.keys(uploadFields).forEach(key => {
    formData.append(key, uploadFields[key]);
  });
  formData.append('file', fileToUpload);

  // Upload directly to S3
  const uploadResponse = await fetch(uploadURL, {
    method: 'POST',
    body: formData
  });

  if (!uploadResponse.ok) {
    // S3 returns 403 when Content-Length-Range is exceeded
    if (uploadResponse.status === 403) {
      throw new Error('File too large — rejected by server');
    }
    throw new Error(`S3 upload failed (${uploadResponse.status})`);
  }

  // Construct final S3 URL
  const finalUrl = uploadURL.endsWith('/')
    ? `${uploadURL}${uploadFields.key}`
    : `${uploadURL}/${uploadFields.key}`;

  return {
    url: finalUrl,
    key: uploadFields.key,
    name: safeName,
    size: fileToUpload.size,
    type: fileToUpload.type || file.type
  };
}

/**
 * Delete file(s) from S3 via the /delete-files endpoint.
 *
 * @param {Object} params
 * @param {string} params.bucket   - S3 bucket name
 * @param {string[]} params.keys   - Array of S3 object keys to delete
 * @returns {Promise}
 */
export async function deleteFromS3({ bucket, keys }) {
  return axios.post('/delete-files', {
    bucket,
    fileKeys: JSON.stringify(keys)
  }, {
    headers: { 'Content-Type': 'application/json' }
  });
}

// Map of MIME types to user-friendly file extensions for the file picker
const MIME_TO_EXTENSIONS = {
  'image/jpeg': '.jpg', 'image/jpg': '.jpg', 'image/png': '.png', 'image/gif': '.gif',
  'image/webp': '.webp', 'image/svg+xml': '.svg',
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'text/csv': '.csv', 'text/plain': '.txt',
  'video/mp4': '.mp4', 'video/webm': '.webm', 'video/quicktime': '.mov',
  'audio/mpeg': '.mp3', 'audio/wav': '.wav', 'audio/ogg': '.ogg', 'audio/webm': '.weba'
};

/**
 * Get a comma-separated string of accepted file extensions (for file input accept attribute).
 * Using extensions instead of MIME types gives browsers a more readable file picker filter.
 * @returns {string}
 */
export function getAcceptedFileTypes() {
  const allTypes = Object.values(FILE_CATEGORIES).flatMap(c => c.types);
  const extensions = [...new Set(allTypes.map(t => MIME_TO_EXTENSIONS[t] || t))];
  return extensions.join(',');
}

/**
 * Get a human-readable description of allowed file types (for tooltips/UI).
 * @returns {string}
 */
export function getAcceptedFileTypesDescription() {
  return 'Images (JPG, PNG, GIF, WebP, SVG), Documents (PDF, Word, Excel, CSV, TXT), Video (MP4, WebM, MOV), Audio (MP3, WAV, OGG)';
}

/**
 * Get accepted MIME types for specific categories only.
 * @param {string[]} categories - e.g. ['image', 'document']
 * @returns {string}
 */
export function getAcceptedFileTypesFor(categories) {
  const types = categories.flatMap(cat => (FILE_CATEGORIES[cat] || {}).types || []);
  return types.join(',');
}

/**
 * Get the default FILE_CATEGORIES config (read-only reference).
 * @returns {Object}
 */
export function getFileCategoryConfig() {
  return { ...FILE_CATEGORIES };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Sanitize a filename for safe S3 storage.
 * Matches the server-side common.sanitizeS3Filename() logic.
 */
function sanitizeFilename(name) {
  if (!name) return `file-${Date.now()}`;
  return name
    .replace(/[&#+;?@!$%^={}|\\<>'"`,()[\]]/g, '_')
    .replace(/ /g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/_{2,}/g, '_')
    .replace(/^[-_]+|[-_]+$/g, '');
}

/**
 * Format bytes as human-readable text.
 */
function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  const units = ['KB', 'MB', 'GB'];
  let u = -1;
  do {
    bytes /= 1024;
    ++u;
  } while (bytes >= 1024 && u < units.length - 1);
  return bytes.toFixed(1) + ' ' + units[u];
}

export default {
  FILE_CATEGORIES,
  getFileCategory,
  validateFile,
  uploadToS3,
  deleteFromS3,
  getAcceptedFileTypes,
  getAcceptedFileTypesDescription,
  getAcceptedFileTypesFor,
  getFileCategoryConfig
};
